import React from 'react';
import { useQuery, useMutation, queryCache } from 'react-query';

import { getUserProps, postBulkUserProps } from '@yandex-int/k-common/client/api';
import type { GetUserPropsType, PostUserPropItem, PostUserPropValueType } from '@yandex-int/k-common/typings';

import { useMe, useAppContext } from 'platform-components';

export type BetaFeatures = Pick<GetUserPropsType, 'teacherMultiAssignmentEnabled'>;

export interface BetaFeatureStatus {
    readonly isActive: boolean;
    readonly setActive: (active: boolean) => Promise<void>;
    readonly setActivePending: boolean;
}

export function useBetaFeature(betaFeatureKey: keyof BetaFeatures): BetaFeatureStatus {
    const meId = useMe('id');
    const getUserPropsQueryKey = ['getUserProps', meId] as const;
    const { data: userProps } = useQuery(getUserPropsQueryKey, {
        enabled: meId !== undefined,
        queryFn: () => getUserProps(),
    });
    const {
        ssrData: { userProps: ssrUserProps },
    } = useAppContext();

    const [setActive, { isLoading: setActivePending }] = useMutation(
        ([key, active]: [keyof BetaFeatures, boolean]) => {
            const item = makeBetaFeatureSetter(key, active);
            return postBulkUserProps({ values: [item] });
        },
        {
            onSuccess: () => {
                queryCache.invalidateQueries(getUserPropsQueryKey);
            },
        }
    );

    return React.useMemo((): BetaFeatureStatus => {
        return {
            isActive: Boolean((userProps || (isSSR ? ssrUserProps : undefined))?.[betaFeatureKey]?.value),
            setActive: (active) => setActive([betaFeatureKey, active]),
            setActivePending: setActivePending,
        };
    }, [betaFeatureKey, userProps, setActive, setActivePending]);
}

type BetaFeatureSetterName = Exclude<BetaFeatures[keyof BetaFeatures], undefined>['name'];
type BetaFeatureSetter = PostUserPropItem<Pick<PostUserPropValueType, BetaFeatureSetterName>>;

function makeBetaFeatureSetter(key: keyof BetaFeatures, value: boolean): BetaFeatureSetter {
    switch (key) {
        case 'teacherMultiAssignmentEnabled': {
            return { name: 'teacher_multi_assignment_enabled', value };
        }
        default: {
            throw new Error(`Unknown beta feature key: ${key}`);
        }
    }
}
